import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, AfterViewInit, Output, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import * as EL from 'esri-leaflet';
import * as KML from 'src/app/components/map/leaflet.kml';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { IconService } from 'src/service/icon.service';
import { Camera, Incident } from 'src/api/models';
import { CameraFovOverlay } from './camera-fov-overlay';

@Component({
  selector: 'app-command-centre-map',
  templateUrl: './command-centre-map.component.html',
  styleUrls: ['./command-centre-map.component.css']
})
export class CommandCentreMapComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mapDiv', { static: false }) protected mapDivRef: ElementRef;

  @Input() mapheight: string = '500px';
  @Input() cameraSegmentCounts: { [cameraName: string]: number } = {};
  /** Seconds after last image before a segment turns amber (default 60). */
  @Input() coldThreshold: number = 60;
  /** Seconds after last image before a segment turns crimson (default 300). */
  @Input() overdueThreshold: number = 300;
  @Output() mapReady = new EventEmitter<L.Map>();
  @Output() segmentClicked = new EventEmitter<{ cameraUri: string; segmentIndex: number }>();

  public map: L.Map;
  private lastLayer: L.TileLayer;
  private init = false;

  private cartoDark: L.TileLayer;
  private cartoLight: L.TileLayer;
  private googleSat: L.TileLayer;
  private openStreetMap: L.TileLayer;

  constructor(private configService: ApplicationConfigService, private icons: IconService) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.initMap();
  }

  private initMap(): void {
    if (this.init || !this.mapDivRef) { return; }
    this.init = true;

    const center: [number, number] = this.configService.customer
      ? [this.configService.customer.centreMapLatitude, this.configService.customer.centreMapLongitude]
      : [ 64.575698, -149.530114];

    this.map = L.map(this.mapDivRef.nativeElement, {
      center,
      zoom: 8,
      maxZoom: 20,
      zoomControl: false
    });

    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // Tile layers
    this.cartoDark = L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      }
    );

    this.cartoLight = L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      }
    );

    this.googleSat = L.tileLayer(
      'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      { maxZoom: 20, subdomains: ['mt0', 'mt1', 'mt2', 'mt3'] }
    );

    this.openStreetMap = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      { maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }
    );

    // Default to Carto Dark
    this.setBaseLayer(this.cartoDark);

    // Layer switcher
    const baseLayers = {
      'Dark (Carto)': this.cartoDark,
      'Light (Carto)': this.cartoLight,
      'Satellite': this.googleSat,
      'OpenStreetMap': this.openStreetMap
    };
    L.control.layers(baseLayers, {}, { position: 'bottomleft', collapsed: true }).addTo(this.map);

    this.mapReady.emit(this.map);
  }

  private setBaseLayer(layer: L.TileLayer): void {
    if (this.lastLayer) {
      this.map.removeLayer(this.lastLayer);
    }
    layer.addTo(this.map);
    this.lastLayer = layer;
  }

  private cameraOverlays = new Map<string, CameraFovOverlay>();
  private incidentMarkers = new Map<number, L.Marker>();

  /** Render a futuristic FOV HUD for every camera that has valid coordinates. */
  public displayCameras(cameras: Camera[], onCameraClick?: (camera: Camera) => void): void {
    this.clearCameras();
    if (!this.map || !cameras?.length) { return; }

    for (const camera of cameras) {
      const lat = this.getCameraLat(camera);
      const lng = this.getCameraLng(camera);
      if (lat == null || lng == null) { continue; }

      const name = camera.twin?.siteName ?? camera.name ?? 'Fire Camera';
      const key = camera.twin?.siteUri ?? camera.siteUri ?? `${lat},${lng}`;
      const segmentCount = this.cameraSegmentCounts[camera.name] ?? 12;
      const cameraUri = key;
      const fov = new CameraFovOverlay(
        this.map, lat, lng, name, segmentCount,
        this.coldThreshold, this.overdueThreshold,
        (segmentIndex) => this.segmentClicked.emit({ cameraUri, segmentIndex })
      );
      this.cameraOverlays.set(key, fov);
    }
  }

  public clearCameras(): void {
    this.cameraOverlays.forEach(fov => fov.remove());
    this.cameraOverlays.clear();
  }

  /** Render a marker for each incident that has a known location. */
  public displayIncidents(incidents: Incident[], onIncidentClick?: (incident: Incident) => void): void {
    this.clearIncidents();
    if (!this.map || !incidents?.length) { return; }

    for (const incident of incidents) {
      const lat = incident.initialLocationLat;
      const lng = incident.initialLocationLon;
      if (lat == null || lng == null || lat === 0 || lng === 0) { continue; }

      const icon = this.getIncidentIcon(incident);
      const marker = L.marker([lat, lng], { icon });

      const title = incident.incidentTitle ?? incident.incidentReference ?? `Incident #${incident.incidentId}`;
      const status = incident.status?.displayName ?? '';
      const date = incident.fireIncidentDate ? new Date(incident.fireIncidentDate).toLocaleDateString() : '';
      const site = incident.site?.name ?? '';
      marker.bindPopup(
        `<div style="font-family:monospace;min-width:160px">
           <b style="color:#ff5000">${title}</b><br>
           ${site ? `<span>${site}</span><br>` : ''}
           ${status ? `<span>Status: ${status}</span><br>` : ''}
           ${date ? `<span>${date}</span>` : ''}
         </div>`,
        { className: 'cc-incident-popup' }
      );

      if (onIncidentClick) {
        marker.on('click', () => onIncidentClick(incident));
      }

      marker.addTo(this.map);
      this.incidentMarkers.set(incident.incidentId, marker);
    }
  }

  /** Get incident marker icon based on status */
  private getIncidentIcon(incident: Incident): L.DivIcon {
    const status = incident.status?.displayName?.toLowerCase() ?? '';
    
    // Default to orange (confirmed)
    let color = '#ff5000';
    let pulseClass = '';
    let crossClass = '';
    let pulseColor = color;
    
    // Determine color and effects based on status
    if (status.includes('investigating')) {
      color = '#3b82f6'; // Blue
    } else if (status.includes('watching')) {
      color = '#3b82f6'; // Blue
      pulseClass = ' incident-marker-pulse';
      pulseColor = color;
    } else if (status.includes('confirmed') && !status.includes('ignore')) {
      color = '#ff5000'; // Orange (current)
    } else if (status.includes('ignore') || status.includes('temporary ignore')) {
      color = '#6b7280'; // Grey
      crossClass = ' incident-marker-cross';
    } else if (status.includes('duplicate')) {
      color = '#d946ef'; // Magenta
    } else if (status.includes('escalated')) {
      color = '#ef4444'; // Red
    } else if (status.includes('not a fire') || status.includes('not fire')) {
      color = '#6b7280'; // Grey
      crossClass = ' incident-marker-cross';
    } else if (status.includes('acknowledged')) {
      color = '#ef4444'; // Red
      pulseClass = ' incident-marker-pulse';
      pulseColor = '#3b82f6'; // Blue pulse
    }

    const html = `<div class="${pulseClass}${crossClass}" style="width:20px;height:20px;background:rgba(${this.hexToRgb(color)},0.85);border:2px solid ${color};border-radius:50%;box-shadow:0 0 8px ${color};cursor:pointer;${pulseClass ? `color:${pulseColor};` : ''}"></div>`;

    return L.divIcon({
      className: '',
      html,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  }

  /** Convert hex color to rgb values for rgba */
  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`
      : '255,80,0';
  }

  public clearIncidents(): void {
    this.incidentMarkers.forEach(m => m.remove());
    this.incidentMarkers.clear();
  }

  /** Update a single incident marker when its status or location changes */
  public updateIncidentMarker(incident: Incident): void {
    if (!this.map) { return; }

    const lat = incident.initialLocationLat;
    const lng = incident.initialLocationLon;
    const hasValidLocation = lat != null && lng != null && lat !== 0 && lng !== 0;
    
    const existingMarker = this.incidentMarkers.get(incident.incidentId);
    
    // If no valid location, remove marker if it exists and return
    if (!hasValidLocation) {
      if (existingMarker) {
        existingMarker.remove();
        this.incidentMarkers.delete(incident.incidentId);
      }
      return;
    }

    // Remove old marker if it exists
    if (existingMarker) {
      existingMarker.remove();
    }
    
    // Create new marker with updated icon and location
    const icon = this.getIncidentIcon(incident);
    const marker = L.marker([lat, lng], { icon });

    const title = incident.incidentTitle ?? incident.incidentReference ?? `Incident #${incident.incidentId}`;
    const status = incident.status?.displayName ?? '';
    const date = incident.fireIncidentDate ? new Date(incident.fireIncidentDate).toLocaleDateString() : '';
    const site = incident.site?.name ?? '';
    marker.bindPopup(
      `<div style="font-family:monospace;min-width:160px">
         <b style="color:#ff5000">${title}</b><br>
         ${site ? `<span>${site}</span><br>` : ''}
         ${status ? `<span>Status: ${status}</span><br>` : ''}
         ${date ? `<span>${date}</span>` : ''}
       </div>`,
      { className: 'cc-incident-popup' }
    );

    marker.addTo(this.map);
    this.incidentMarkers.set(incident.incidentId, marker);
  }

  /**
   * Update a single camera segment — call this from SignalR handlers.
   * @param cameraUri  The camera's siteUri (used as the map key)
   * @param segmentIndex  0–9, where 0 = North, clockwise
   * @param timestamp  Optional epoch ms; defaults to now
   */
  public updateCameraSegment(cameraUri: string, segmentIndex: number, timestamp?: number): void {
    this.cameraOverlays.get(cameraUri)?.updateSegment(segmentIndex, timestamp);
  }

  private getCameraLat(camera: Camera): number {
    if (camera.latitude != null && camera.latitude >= -90 && camera.latitude <= 90) { return camera.latitude; }
    if (camera.twin?.latitude != null && camera.twin.latitude >= -90 && camera.twin.latitude <= 90) { return camera.twin.latitude; }
    return null;
  }

  private getCameraLng(camera: Camera): number {
    if (camera.longitude != null && camera.longitude >= -180 && camera.longitude <= 180) { return camera.longitude; }
    if (camera.twin?.longitude != null && camera.twin.longitude >= -180 && camera.twin.longitude <= 180) { return camera.twin.longitude; }
    return null;
  }

  /** Trigger a Leaflet size recalculation (e.g. after a container resize). */
  public redraw(): void {
    this.map?.invalidateSize();
  }

  public removeLayer(layer: any): void {
    if (layer) { this.map?.removeLayer(layer); }
  }

  public loadLinkLayerContent(type: string, layerId: number, json: any, layerName: string, styles: string, tileSize: number, useProxy: boolean, onClick: ((f: any) => void) | null): any {
    if (!this.map) { return null; }

    if (type === 'wms') {
      let url = json;
      if (useProxy) { url = '/api/Map/Proxy/' + layerId; }
      tileSize = Math.min(Math.max(tileSize || 256, 256), 2048);
      return L.tileLayer.wms(url, {
        layers: layerName, transparent: true, crossOrigin: false,
        styles: styles ?? '', tileSize, format: 'image/png'
      }).addTo(this.map);
    }

    if (type === 'kml') {
      const kml = KML.layer(json);
      kml.addTo(this.map);
      return kml;
    }

    if (type === 'kmz') {
      const kml = KML.layerFromKMZ(json);
      kml.addTo(this.map);
      return kml;
    }

    if (type === 'geojson') {
      const styleObj = (styles && styles !== '') ? JSON.parse(styles) : {};
      return L.geoJson(json, {
        pointToLayer: (feature, latlng) => {
          if (feature?.properties?.type?.eventIcon) {
            const alertLevel = feature.properties.alertLevel;
            const icon = feature.properties.type.eventIcon;
            let iconUrl = `/assets/icons/${icon}.svg`;
            if (alertLevel === 'emergency_warning') { iconUrl = `/assets/icons/${icon}_red.svg`; }
            else if (alertLevel === 'watch_and_act') { iconUrl = `/assets/icons/${icon}_orange.svg`; }
            else if (alertLevel === 'advice')        { iconUrl = `/assets/icons/${icon}_yellow.svg`; }
            const marker = L.marker(latlng, { icon: L.icon({ iconUrl, iconSize: [38, 95] }) });
            if (onClick) { marker.on('click', () => onClick(feature)); }
            return marker;
          }
          if (feature?.properties?.style) {
            return L.circleMarker(latlng, { radius: 8, fillOpacity: 0.85, weight: 1, ...feature.properties.style });
          }
          return L.circleMarker(latlng);
        },
        onEachFeature: (feature, layer) => {
          const props = feature.properties;
          if (!props || typeof props !== 'object') { return; }
          const content = Object.entries(props)
            .filter(([k, v]) => v != null && k !== 'style')
            .map(([k, v]) => `<b>${k}</b>: ${typeof v === 'object' && !Array.isArray(v) ? '[Object]' : v}`)
            .join('<br>');
          if (content) { layer.bindTooltip(content); }
        },
        style: styleObj
      }).addTo(this.map);
    }

    if (type === 'esri') {
      const fl = EL.featureLayer({ url: json, useCors: true });
      fl.addTo(this.map);
      return fl;
    }

    if (type === 'cap') {
      return L.geoJson(json, {
        pointToLayer: (feature, latlng) => {
          if (feature?.properties) {
            const alertLevel = feature.properties.alertLevel;
            const icon = feature.properties.type?.eventIcon;
            let iconUrl = `/assets/icons/${icon}.svg`;
            if (alertLevel === 'emergency_warning') { iconUrl = `/assets/icons/${icon}_red.svg`; }
            else if (alertLevel === 'watch_and_act') { iconUrl = `/assets/icons/${icon}_orange.svg`; }
            else if (alertLevel === 'advice')        { iconUrl = `/assets/icons/${icon}_yellow.svg`; }
            const marker = L.marker(latlng, { icon: L.icon({ iconUrl, iconSize: [38, 95] }) });
            if (onClick) { marker.on('click', () => onClick(feature)); }
            return marker;
          }
          return L.marker(latlng);
        }
      }).addTo(this.map);
    }

    if (type === 'tilemap') {
      return L.tileLayer(json, { maxZoom: 20 }).addTo(this.map);
    }

    if (type === 'waemergency') {
      const items = json?.incidents ?? json?.warnings;
      if (!items) { return null; }
      return L.geoJson({ type: 'FeatureCollection', features: items }, {
        pointToLayer: (feature, latlng) => {
          const iconName = feature.properties?.['incident-icon']
            ?? feature.properties?.['geo-source']?.features?.[0]?.properties?.['icon-name'];
          const iconUrl = iconName ? `assets/icons/waemergency/${iconName}.svg` : 'assets/icons/fire.svg';
          const marker = L.marker(latlng, { icon: L.icon({ iconUrl, iconSize: [38, 95] }) });
          if (onClick) { marker.on('click', () => onClick(feature)); }
          return marker;
        }
      }).addTo(this.map);
    }

    return null;
  }

  ngOnDestroy(): void {
    this.clearCameras();
    this.clearIncidents();
    this.map?.remove();
  }
}
